import React, { createContext, useContext, useState, useEffect } from 'react';

const AdsContext = createContext();

export const useAds = () => {
  const context = useContext(AdsContext);
  if (!context) {
    throw new Error('useAds must be used within an AdsProvider');
  }
  return context;
};

const defaultAds = [
  {
    id: 1,
    name: 'Bannière Header - Royal Canin',
    type: 'banner', // banner, sidebar, inline
    position: 'header', // header, sidebar, article-middle, article-end, footer
    imageUrl: 'https://images.unsplash.com/photo-1568640347023-a616a30bc3bd?w=1200&h=200&fit=crop',
    linkUrl: 'https://www.royalcanin.com',
    altText: 'Royal Canin - Nutrition sur mesure',
    advertiser: 'Royal Canin',
    price: 500,
    startDate: '2025-01-01',
    endDate: '2025-03-31',
    active: true,
    clicks: 245,
    impressions: 12500
  },
  {
    id: 2,
    name: 'Sidebar - Assurance Animaux',
    type: 'sidebar',
    position: 'sidebar',
    imageUrl: 'https://images.unsplash.com/photo-1450778869180-41d0601e046e?w=300&h=250&fit=crop',
    linkUrl: 'https://www.santevet.com',
    altText: 'SantéVet - Assurance pour animaux',
    advertiser: 'SantéVet',
    price: 300,
    startDate: '2025-01-01',
    endDate: '2025-06-30',
    active: true,
    clicks: 89,
    impressions: 8700
  },
  {
    id: 3,
    name: 'Article Milieu - Purina',
    type: 'inline',
    position: 'article-middle',
    imageUrl: 'https://images.unsplash.com/photo-1589924691995-400dc9ecc119?w=800&h=200&fit=crop',
    linkUrl: 'https://www.purina.fr',
    altText: 'Purina - Alimentation premium',
    advertiser: 'Purina',
    price: 350,
    startDate: '2025-01-01',
    endDate: '2025-04-30',
    active: true,
    clicks: 156,
    impressions: 9200
  },
  {
    id: 4,
    name: 'Article Fin - Animalis',
    type: 'inline',
    position: 'article-end',
    imageUrl: 'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?w=800&h=200&fit=crop',
    linkUrl: 'https://www.animalis.com',
    altText: 'Animalis - Tout pour vos animaux',
    advertiser: 'Animalis',
    price: 400,
    startDate: '2025-01-01',
    endDate: '2025-12-31',
    active: false,
    clicks: 0,
    impressions: 0
  }
];

export const AdsProvider = ({ children }) => {
  const [ads, setAds] = useState(() => {
    const saved = localStorage.getItem('vetordv_ads');
    return saved ? JSON.parse(saved) : defaultAds;
  });

  useEffect(() => {
    localStorage.setItem('vetordv_ads', JSON.stringify(ads));
  }, [ads]);

  const addAd = (ad) => {
    const newAd = {
      ...ad,
      id: Date.now(),
      clicks: 0,
      impressions: 0
    };
    setAds(prev => [...prev, newAd]);
    return newAd;
  };

  const updateAd = (id, updates) => {
    setAds(prev => prev.map(ad => ad.id === id ? { ...ad, ...updates } : ad));
  };

  const deleteAd = (id) => {
    setAds(prev => prev.filter(ad => ad.id !== id));
  };

  const toggleAdActive = (id) => {
    setAds(prev => prev.map(ad => ad.id === id ? { ...ad, active: !ad.active } : ad));
  };

  const recordClick = (id) => {
    setAds(prev => prev.map(ad => ad.id === id ? { ...ad, clicks: ad.clicks + 1 } : ad));
  };

  const recordImpression = (id) => {
    setAds(prev => prev.map(ad => ad.id === id ? { ...ad, impressions: ad.impressions + 1 } : ad));
  };

  const getAdsByPosition = (position) => {
    const now = new Date();
    return ads.filter(ad => 
      ad.position === position && 
      ad.active &&
      new Date(ad.startDate) <= now &&
      new Date(ad.endDate) >= now
    );
  };

  const getActiveAds = () => ads.filter(ad => ad.active);

  const getStats = () => {
    const activeAds = ads.filter(ad => ad.active);
    return {
      total: ads.length,
      active: activeAds.length,
      totalRevenue: ads.reduce((acc, ad) => acc + ad.price, 0),
      totalClicks: ads.reduce((acc, ad) => acc + ad.clicks, 0),
      totalImpressions: ads.reduce((acc, ad) => acc + ad.impressions, 0),
      avgCTR: ads.reduce((acc, ad) => acc + ad.impressions, 0) > 0 
        ? ((ads.reduce((acc, ad) => acc + ad.clicks, 0) / ads.reduce((acc, ad) => acc + ad.impressions, 0)) * 100).toFixed(2)
        : 0
    };
  };

  const resetToDefault = () => {
    setAds(defaultAds);
  };

  return (
    <AdsContext.Provider value={{
      ads,
      addAd,
      updateAd,
      deleteAd,
      toggleAdActive,
      recordClick,
      recordImpression,
      getAdsByPosition,
      getActiveAds,
      getStats,
      resetToDefault
    }}>
      {children}
    </AdsContext.Provider>
  );
};

export default AdsProvider;
